// ======================================================================
// FILE:
// src/modules/finance/transactions/manual_journal/pages/JournalCreatePage.js
//
// MODULE:
// Finance & Accounts Management System (FBAMS)
//
// FEATURE:
// Manual Journal - Create Journal
//
// PURPOSE:
// Main Create Journal transaction page.
//
// RESPONSIBILITIES:
// - Maintain complete journal transaction state
// - Render Finance page header
// - Render Journal Header
// - Render Journal Lines
// - Render Journal Totals
// - Create journal header
// - Create journal lines
// - Submit journal for approval
// - Navigate back to Journal List
// - Perform frontend validation before API submission
//
// ARCHITECTURE:
// - React functional component
// - Existing Finance components reused
// - Existing journalService reused
// - Existing financeLookupService reused
// - No Redux
// - API calls handled through service layer
//
// IMPORTANT:
// - Reference No. and Reference Date are excluded.
// - Journal No. is generated by backend.
// - Backend remains authoritative for accounting validation.
// - Financial Period depends on Financial Year.
// - Account / Cost Center are filtered by selected Company.
// - Currency is loaded from Finance Currency lookup API.
// ======================================================================

import React, {
    useEffect,
    useMemo,
    useState,
} from "react";

import {
    useNavigate,
} from "react-router-dom";

// ======================================================================
// FINANCE COMMON COMPONENTS
// ======================================================================

import FinancePageHeader
    from "../../../components/FinancePageHeader";

// ======================================================================
// JOURNAL COMPONENTS
// ======================================================================

import JournalHeaderForm
    from "../../../components/JournalHeaderForm";

import JournalLinesTable
    from "../../../components/JournalLinesTable";

import JournalTotals
    from "../../../components/JournalTotals";

// ======================================================================
// JOURNAL SERVICE
// ======================================================================

import journalService
    from "../../../services/journalService";

// ======================================================================
// FINANCE LOOKUP SERVICE
// ======================================================================

import financeLookupService
    from "../../../services/financeLookupService";

// ======================================================================
// JOURNAL TYPE OPTIONS
// ======================================================================

const JOURNAL_TYPE_OPTIONS = [

    {
        value: "JOURNAL",
        label: "General Journal",
    },

    {
        value: "PAYMENT",
        label: "Payment",
    },

    {
        value: "RECEIPT",
        label: "Receipt",
    },

    {
        value: "SALES",
        label: "Sales",
    },

    {
        value: "PURCHASE",
        label: "Purchase",
    },

    {
        value: "OPENING",
        label: "Opening",
    },

    {
        value: "REVERSAL",
        label: "Reversal",
    },

];

// ======================================================================
// INITIAL HEADER
// ======================================================================

const INITIAL_FORM = {

    company_id:
        null,

    financial_year_id:
        null,

    financial_period_id:
        null,

    journal_date:
        new Date()
            .toISOString()
            .slice(
                0,
                10
            ),

    journal_type:
        "JOURNAL",

    currency_id:
        null,

    exchange_rate:
        "1",

    remarks:
        "",

    // --------------------------------------------------------------
    // React Select selected-option objects.
    //
    // These are UI state only and are NOT sent to backend.
    // --------------------------------------------------------------

    company_option:
        null,

    financial_year_option:
        null,

    financial_period_option:
        null,

    currency_option:
        null,

};

// ======================================================================
// INITIAL JOURNAL LINES
// ======================================================================

const INITIAL_LINES = [

    {
        id:
            null,

        line_no:
            10,

        coa_id:
            null,

        account_option:
            null,

        cost_center_id:
            null,

        cost_center_option:
            null,

        description:
            "",

        debit_amount:
            "",

        credit_amount:
            "",

    },

    {
        id:
            null,

        line_no:
            20,

        coa_id:
            null,

        account_option:
            null,

        cost_center_id:
            null,

        cost_center_option:
            null,

        description:
            "",

        debit_amount:
            "",

        credit_amount:
            "",

    },

];

// ======================================================================
// COMPONENT
// ======================================================================

export default function JournalCreatePage() {

    const navigate =
        useNavigate();

    // ==================================================================
    // STATE
    // ==================================================================

    const [
        formData,
        setFormData
    ] = useState(
        INITIAL_FORM
    );

    const [
        lines,
        setLines
    ] = useState(
        INITIAL_LINES
    );

    const [
        loading,
        setLoading
    ] = useState(false);

    const [
        errorMessage,
        setErrorMessage
    ] = useState("");

    // ==================================================================
    // JOURNAL NUMBER
    // ==================================================================

    const [
        journalNo,
        setJournalNo
    ] = useState("");

    // ==================================================================
    // CURRENCY OPTIONS
    // ==================================================================

    const [
        currencyOptions,
        setCurrencyOptions
    ] = useState([]);

    const [
        currencyLoading,
        setCurrencyLoading
    ] = useState(false);

    // ==================================================================
    // LOAD CURRENCIES
    // ==================================================================

    useEffect(
        () => {

            let cancelled = false;

            const loadCurrencies =
                async () => {

                    try {

                        setCurrencyLoading(
                            true
                        );

                        const options =
                            await financeLookupService.searchCurrencies(
                                "",
                                100
                            );

                        if (
                            !cancelled
                        ) {

                            setCurrencyOptions(
                                Array.isArray(options)
                                    ? options
                                    : []
                            );

                        }

                    } catch (
                        error
                    ) {

                        console.error(
                            "Failed to load currencies:",
                            error
                        );

                        if (
                            !cancelled
                        ) {

                            setCurrencyOptions(
                                []
                            );

                        }

                    } finally {

                        if (
                            !cancelled
                        ) {

                            setCurrencyLoading(
                                false
                            );

                        }

                    }

                };

            loadCurrencies();

            return () => {

                cancelled = true;

            };

        },
        []
    );

    // ==================================================================
    // HEADER FIELD CHANGE
    // ==================================================================

    const handleFieldChange = (
        fieldName,
        value,
        selectedOption = null
    ) => {

        setFormData(
            previous => {

                const updated = {

                    ...previous,

                    [fieldName]:
                        value,

                };

                // ------------------------------------------------------
                // Preserve selected lookup object.
                // ------------------------------------------------------

                if (
                    fieldName ===
                    "company_id"
                ) {

                    updated.company_option =
                        selectedOption;

                    // --------------------------------------------------
                    // Company changed.
                    //
                    // Existing dependent selections must be cleared.
                    // --------------------------------------------------

                    updated.financial_year_id =
                        null;

                    updated.financial_year_option =
                        null;

                    updated.financial_period_id =
                        null;

                    updated.financial_period_option =
                        null;

                }

                if (
                    fieldName ===
                    "financial_year_id"
                ) {

                    updated.financial_year_option =
                        selectedOption;

                    // --------------------------------------------------
                    // Period belongs to Financial Year.
                    // Clear old period whenever FY changes.
                    // --------------------------------------------------

                    updated.financial_period_id =
                        null;

                    updated.financial_period_option =
                        null;

                }

                if (
                    fieldName ===
                    "financial_period_id"
                ) {

                    updated.financial_period_option =
                        selectedOption;

                }

                if (
                    fieldName ===
                    "currency_id"
                ) {

                    updated.currency_option =
                        selectedOption;

                }

                return updated;

            }
        );

        // --------------------------------------------------------------
        // Clear previous error when user changes data.
        // --------------------------------------------------------------

        if (
            errorMessage
        ) {

            setErrorMessage(
                ""
            );

        }

    };

    // ==================================================================
    // ACCOUNT LOOKUP
    // ==================================================================
    //
    // Account lookup is company-specific.
    //
    // Journal Lines Table expects:
    //
    // loadAccounts(inputValue)
    //
    // and receives React Select options:
    //
    // {
    //     value,
    //     label
    // }
    //
    // ==================================================================

    const loadAccounts =
        async (
            inputValue = ""
        ) => {

            if (
                !formData.company_id
            ) {

                return [];

            }

            try {

                return await financeLookupService
                    .searchChartOfAccounts(
                        formData.company_id,
                        inputValue,
                        null,
                        20
                    );

            } catch (
                error
            ) {

                console.error(
                    "Failed to load chart of accounts:",
                    error
                );

                return [];

            }

        };

    // ==================================================================
    // COST CENTER LOOKUP
    // ==================================================================
    //
    // Cost Center lookup is company-specific.
    //
    // Journal Lines Table expects:
    //
    // loadCostCenters(inputValue)
    //
    // ==================================================================

    const loadCostCenters =
        async (
            inputValue = ""
        ) => {

            if (
                !formData.company_id
            ) {

                return [];

            }

            try {

                return await financeLookupService
                    .searchCostCenters(
                        formData.company_id,
                        inputValue,
                        20
                    );

            } catch (
                error
            ) {

                console.error(
                    "Failed to load cost centers:",
                    error
                );

                return [];

            }

        };

    // ==================================================================
    // BASIC FRONTEND VALIDATION
    // ==================================================================

    const validateJournal =
        () => {

            const errors = [];

            // ----------------------------------------------------------
            // HEADER
            // ----------------------------------------------------------

            if (
                !formData.company_id
            ) {

                errors.push(
                    "Company is required."
                );

            }

            if (
                !formData.financial_year_id
            ) {

                errors.push(
                    "Financial Year is required."
                );

            }

            if (
                !formData.financial_period_id
            ) {

                errors.push(
                    "Financial Period is required."
                );

            }

            if (
                !formData.journal_date
            ) {

                errors.push(
                    "Journal Date is required."
                );

            }

            if (
                !formData.journal_type
            ) {

                errors.push(
                    "Journal Type is required."
                );

            }

            if (
                !formData.currency_id
            ) {

                errors.push(
                    "Currency is required."
                );

            }

            if (
                !formData.exchange_rate ||
                Number(
                    formData.exchange_rate
                ) <= 0
            ) {

                errors.push(
                    "Exchange Rate must be greater than zero."
                );

            }

            if (
                !formData.remarks?.trim()
            ) {

                errors.push(
                    "Narration is required."
                );

            }

            // ----------------------------------------------------------
            // LINES
            // ----------------------------------------------------------

            if (
                lines.length < 2
            ) {

                errors.push(
                    "At least two journal lines are required."
                );

            }

            // ----------------------------------------------------------
            // LINE VALIDATION
            // ----------------------------------------------------------

            lines.forEach(
                (
                    line,
                    index
                ) => {

                    const lineNumber =
                        index + 1;

                    if (
                        !line.coa_id
                    ) {

                        errors.push(
                            `Account is required for line ${lineNumber}.`
                        );

                    }

                    const debit =
                        Number(
                            line.debit_amount
                        ) || 0;

                    const credit =
                        Number(
                            line.credit_amount
                        ) || 0;

                    if (
                        debit < 0 ||
                        credit < 0
                    ) {

                        errors.push(
                            `Debit/Credit cannot be negative on line ${lineNumber}.`
                        );

                    }

                    if (
                        debit > 0 &&
                        credit > 0
                    ) {

                        errors.push(
                            `Line ${lineNumber} cannot have both Debit and Credit.`
                        );

                    }

                    if (
                        debit === 0 &&
                        credit === 0
                    ) {

                        errors.push(
                            `Line ${lineNumber} must have either Debit or Credit.`
                        );

                    }

                }
            );

            // ----------------------------------------------------------
            // BALANCE
            // ----------------------------------------------------------

            const totalDebit =
                lines.reduce(
                    (
                        total,
                        line
                    ) =>
                        total +
                        (
                            Number(
                                line.debit_amount
                            ) || 0
                        ),
                    0
                );

            const totalCredit =
                lines.reduce(
                    (
                        total,
                        line
                    ) =>
                        total +
                        (
                            Number(
                                line.credit_amount
                            ) || 0
                        ),
                    0
                );

            if (
                Math.abs(
                    totalDebit -
                    totalCredit
                ) > 0.000001
            ) {

                errors.push(
                    "Total Debit must equal Total Credit."
                );

            }

            return errors;

        };

    // ==================================================================
    // BUILD HEADER PAYLOAD
    // ==================================================================

    const buildHeaderPayload =
        () => ({

            company_id:
                Number(
                    formData.company_id
                ),

            financial_year_id:
                Number(
                    formData.financial_year_id
                ),

            financial_period_id:
                Number(
                    formData.financial_period_id
                ),

            journal_date:
                formData.journal_date,

            journal_type:
                formData.journal_type,

            currency_id:
                Number(
                    formData.currency_id
                ),

            exchange_rate:
                Number(
                    formData.exchange_rate
                ),

            remarks:
                formData.remarks
                    ?.trim() || "",

        });

    // ==================================================================
    // BUILD LINE PAYLOAD
    // ==================================================================

    const buildLinePayload =
        (
            line
        ) => {

            const debit =
                Number(
                    line.debit_amount
                ) || 0;

            const credit =
                Number(
                    line.credit_amount
                ) || 0;

            const amount =
                debit > 0
                    ? debit
                    : credit;

            return {

                coa_id:
                    Number(
                        line.coa_id
                    ),

                cost_center_id:
                    line.cost_center_id
                        ? Number(
                            line.cost_center_id
                        )
                        : null,

                debit_amount:
                    debit,

                credit_amount:
                    credit,

                currency_amount:
                    amount,

                description:
                    line.description
                        ?.trim() || "",

                line_reference:
                    null,

            };

        };

    // ==================================================================
    // CREATE JOURNAL
    // ==================================================================

    const createJournal =
        async (
            submitAfterCreate = false
        ) => {

            setErrorMessage(
                ""
            );

            const errors =
                validateJournal();

            if (
                errors.length > 0
            ) {

                setErrorMessage(
                    errors.join(
                        " "
                    )
                );

                return;

            }

            try {

                setLoading(
                    true
                );

                // ------------------------------------------------------
                // STEP 1
                // Create journal header.
                // ------------------------------------------------------

                const createdJournal =
                    await journalService.createJournal(
                        buildHeaderPayload()
                    );

                const headerId =
                    createdJournal?.id ||
                    createdJournal?.journal_header_id;

                if (
                    !headerId
                ) {

                    throw new Error(
                        "Journal was created but Journal ID was not returned by the server."
                    );

                }

                // ------------------------------------------------------
                // Store generated Journal No.
                // ------------------------------------------------------

                setJournalNo(
                    createdJournal?.journal_no ||
                    ""
                );

                // ------------------------------------------------------
                // STEP 2
                // Create journal lines.
                // ------------------------------------------------------

                for (
                    const line
                    of lines
                ) {

                    await journalService.createJournalLine(
                        headerId,
                        buildLinePayload(
                            line
                        )
                    );

                }

                // ------------------------------------------------------
                // STEP 3
                // Submit only when explicitly requested.
                // ------------------------------------------------------

                if (
                    submitAfterCreate
                ) {

                    await journalService.submitJournal(
                        headerId
                    );

                    navigate(
                        "/finance/manual-journal"
                    );

                    return;

                }

                // ------------------------------------------------------
                // Save Draft complete.
                // Go back to list.
                // ------------------------------------------------------

                navigate(
                    "/finance/manual-journal"
                );

            } catch (
                error
            ) {

                console.error(
                    "Failed to create journal:",
                    error
                );

                const message =
                    error?.response?.data?.message ||
                    error?.message ||
                    "Failed to create journal.";

                setErrorMessage(
                    message
                );

            } finally {

                setLoading(
                    false
                );

            }

        };

    // ==================================================================
    // SAVE DRAFT
    // ==================================================================

    const handleSaveDraft =
        () => {

            createJournal(
                false
            );

        };

    // ==================================================================
    // SUBMIT FOR APPROVAL
    // ==================================================================

    const handleSubmit =
        () => {

            createJournal(
                true
            );

        };

    // ==================================================================
    // CANCEL
    // ==================================================================

    const handleCancel =
        () => {

            if (
                loading
            ) {

                return;

            }

            navigate(
                "/finance/manual-journal"
            );

        };

    // ==================================================================
    // RENDER
    // ==================================================================

    return (

        <div className="container-fluid">

            {/* ==========================================================
                PAGE HEADER
            ========================================================== */}

            <FinancePageHeader

                icon="fas fa-file-invoice"

                title="Create Journal"

                subtitle="Create a new manual journal entry"

                breadcrumbs={[
                    {
                        label:
                            "Finance",
                        path:
                            "/finance",
                    },
                    {
                        label:
                            "Manual Journal",
                        path:
                            "/finance/manual-journal",
                    },
                    {
                        label:
                            "Create Journal",
                    },
                ]}

                actions={

                    <div
                        className="
                            d-flex
                            flex-wrap
                            gap-2
                        "
                    >

                        <button
                            type="button"
                            className="
                                btn
                                btn-outline-secondary
                            "
                            onClick={
                                handleCancel
                            }
                            disabled={
                                loading
                            }
                        >
                            Cancel
                        </button>

                        <button
                            type="button"
                            className="
                                btn
                                btn-outline-primary
                            "
                            onClick={
                                handleSaveDraft
                            }
                            disabled={
                                loading
                            }
                        >

                            {
                                loading
                                    ? (
                                        <>
                                            <span
                                                className="
                                                    spinner-border
                                                    spinner-border-sm
                                                    me-1
                                                "
                                            />
                                            Saving...
                                        </>
                                    )
                                    : (
                                        <>
                                            <i
                                                className="
                                                    fas
                                                    fa-save
                                                    me-1
                                                "
                                            />
                                            Save Draft
                                        </>
                                    )
                            }

                        </button>

                        <button
                            type="button"
                            className="
                                btn
                                btn-primary
                            "
                            onClick={
                                handleSubmit
                            }
                            disabled={
                                loading
                            }
                        >

                            <i
                                className="
                                    fas
                                    fa-paper-plane
                                    me-1
                                "
                            />

                            Submit for Approval

                        </button>

                    </div>

                }

            />

            {/* ==========================================================
                ERROR
            ========================================================== */}

            {
                errorMessage && (

                    <div
                        className="
                            alert
                            alert-danger
                            d-flex
                            align-items-start
                        "
                        role="alert"
                    >

                        <i
                            className="
                                fas
                                fa-exclamation-circle
                                me-2
                                mt-1
                            "
                        />

                        <div>
                            {errorMessage}
                        </div>

                    </div>

                )
            }

            {/* ==========================================================
                JOURNAL HEADER
            ========================================================== */}

            <JournalHeaderForm

                formData={
                    formData
                }

                onFieldChange={
                    handleFieldChange
                }

                journalNo={
                    journalNo
                }

                journalTypeOptions={
                    JOURNAL_TYPE_OPTIONS
                }

                currencyOptions={
                    currencyOptions
                }

                selectedCompany={
                    formData.company_option
                }

                selectedFinancialYear={
                    formData.financial_year_option
                }

                selectedFinancialPeriod={
                    formData.financial_period_option
                }

                selectedCurrency={
                    formData.currency_option
                }

                disabled={
                    loading
                }

                loading={
                    loading ||
                    currencyLoading
                }

            />

            {/* ==========================================================
                JOURNAL LINES
            ========================================================== */}

            <JournalLinesTable

                lines={
                    lines
                }

                onLinesChange={
                    setLines
                }

                loadAccounts={
                    loadAccounts
                }

                loadCostCenters={
                    loadCostCenters
                }

                disabled={
                    loading
                }

                loading={
                    loading
                }

            />

            {/* ==========================================================
                SUMMARY & TOTALS
            ========================================================== */}

            <JournalTotals

                lines={
                    lines
                }

                narration={
                    formData.remarks
                }

                attachmentCount={
                    0
                }

            />

            {/* ==========================================================
                BOTTOM ACTIONS
            ========================================================== */}

            <div
                className="
                    d-flex
                    justify-content-end
                    flex-wrap
                    gap-2
                    mb-4
                "
            >

                <button
                    type="button"
                    className="
                        btn
                        btn-outline-secondary
                    "
                    onClick={
                        handleCancel
                    }
                    disabled={
                        loading
                    }
                >
                    Cancel
                </button>

                <button
                    type="button"
                    className="
                        btn
                        btn-outline-primary
                    "
                    onClick={
                        handleSaveDraft
                    }
                    disabled={
                        loading
                    }
                >
                    <i
                        className="
                            fas
                            fa-save
                            me-1
                        "
                    />
                    Save Draft
                </button>

                <button
                    type="button"
                    className="
                        btn
                        btn-primary
                    "
                    onClick={
                        handleSubmit
                    }
                    disabled={
                        loading
                    }
                >
                    <i
                        className="
                            fas
                            fa-paper-plane
                            me-1
                        "
                    />
                    Submit for Approval
                </button>

            </div>

        </div>

    );

}